import { FormElementMeta } from '@people-science/forms-core';

import { FormElementPlugin } from './plugins/FormElementPlugin';

/**
 * Whether a section has everything it was there to collect.
 *
 * Shared by the two places that need to know: the Next button, which stops the
 * participant, and the state machine's canAdvanceFrom, which stops the machine
 * advancing on its own. Those had drifted apart — only the button knew about
 * `required`, so a transition the machine took by itself walked past a question
 * nobody had been shown.
 */
export function hasCompletedSection<FormValuesType, FormElementConfigType>(
  elements:
    | FormElementMeta<FormValuesType, FormElementConfigType>[]
    | undefined,
  formValues: Partial<FormValuesType> | undefined,
  elementPlugins: FormElementPlugin<any, any>[]
): boolean {
  // Plugins are handed the values object directly and predate this being
  // callable outside a mounted section, so never give them undefined.
  const values = formValues || {};
  for (const element of elements || []) {
    const plugin = elementPlugins.find(ep => ep.type === element.type);
    if (plugin?.validate) {
      const valid = plugin.validate(element, values);
      if (!valid) return false;
    }
    const isRequiredValid = element.required
      ? plugin?.validateRequired?.(element, values)
      : undefined;
    if (isRequiredValid === false) return false;
    if (isRequiredValid) continue;
    const value = (values as any)[element.dataSource || element.key];
    const isTextField =
      element.type === 'TextField' || element.type === 'PhoneNumber';

    if (
      element.required &&
      (value === null ||
        typeof value === 'undefined' ||
        // NaN and Infinity are both numbers, so they pass every other check
        // here, and JSON.stringify writes either one as null
        (typeof value === 'number' && !Number.isFinite(value)) ||
        (Array.isArray(value) && value.length === 0) ||
        // trimmed: a space bar is not an answer. Matches the shared rule in
        // forms-core, which this is on course to be replaced by.
        (isTextField && !String(value ?? '').trim()))
    ) {
      return false;
    }
  }
  return true;
}
